import { useMemo } from 'react';
import { useLabel } from '@platform/hooks/useLabel';
import { useWorkspaceParams } from '@platform/hooks/useWorkspaceParams';
import { placement, type ICrumb } from '@platform/navigation';
import { useGetCatalogQuery, useMeQuery } from '@platform/state/api';
import { moduleCrumbs } from './moduleCrumbs';

const NO_TAIL: ICrumb[] = [];

/**
 * Крихти сторінки модуля поточного робочого простору: назва простору — з профілю користувача, група — з моделі навігації v4
 * (placement; власний модуль ADR-025 — за категорією з каталогу простору, тож крихти перераховуються, щойно каталог завантажено),
 * далі модуль (типово — повернення до модуля; `moduleTo` — інша адреса рівня модуля) і `tail` (рівні розділу сторінки). Поки профіль
 * вантажиться, перший рівень — порожній підпис.
 */
export function useModuleCrumbs(moduleKey: string, moduleName: string, tail: ICrumb[] = NO_TAIL, moduleTo?: string): ICrumb[] {
  const { ws } = useWorkspaceParams();
  const label = useLabel();
  const { data: me } = useMeQuery();
  const { data: catalog } = useGetCatalogQuery({ workspaceId: ws }, { skip: !ws });
  const wsName = me?.workspaces.find((w) => w.id === ws)?.name ?? '';
  return useMemo(
    () => moduleCrumbs({ id: ws, name: wsName }, placement(moduleKey), { key: moduleKey, name: moduleName, to: moduleTo }, label, tail),
    // catalog — лише тригер: каталог реєструє власні модулі в моделі навігації, з якої читає placement
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ws, wsName, moduleKey, moduleName, label, tail, moduleTo, catalog],
  );
}
